import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getBookingApiKeys,
  addBookingApiKey,
  deleteBookingApiKey,
} from '../apis/bookingApiKeyApi';
import { AddApiKeyRequest, ToolName } from '../apis/types';

export const useBookingApiKeys = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Query for fetching booking API keys
  const {
    data: apiKeys = [],
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['bookingApiKeys'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getBookingApiKeys(token);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Mutation for adding/updating API key
  const addApiKeyMutation = useMutation({
    mutationFn: async (data: AddApiKeyRequest) => {
      const token = await getAccessTokenSilently();
      return addBookingApiKey(data, token);
    },
    onSuccess: () => {
      // Invalidate and refetch API keys
      queryClient.invalidateQueries({ queryKey: ['bookingApiKeys'] });
    },
    onError: error => {
      console.error('Error adding API key:', error);
    },
  });

  // Mutation for deleting API key
  const deleteApiKeyMutation = useMutation({
    mutationFn: async (toolName: ToolName) => {
      const token = await getAccessTokenSilently();
      return deleteBookingApiKey(toolName, token);
    },
    onSuccess: () => {
      // Invalidate and refetch API keys
      queryClient.invalidateQueries({ queryKey: ['bookingApiKeys'] });
    },
    onError: error => {
      console.error('Error deleting API key:', error);
    },
  });

  const error =
    queryError?.message ||
    addApiKeyMutation.error?.message ||
    deleteApiKeyMutation.error?.message ||
    null;

  return {
    apiKeys,
    loading,
    adding: addApiKeyMutation.isPending,
    deleting: deleteApiKeyMutation.isPending,
    error,
    addApiKey: addApiKeyMutation.mutateAsync,
    deleteApiKey: deleteApiKeyMutation.mutateAsync,
    refresh: () => {
      queryClient.invalidateQueries({ queryKey: ['bookingApiKeys'] });
    },
  };
};
